import React, { useEffect, useState } from 'react';
import { Button, Space } from 'antd';
import { LoginOutlined, UserAddOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { siteAPI } from '../services/api';
import { useSiteBranding } from '../hooks/useSiteBranding';

const NAVY = '#0B2545';
const GOLD = '#C5A55A';

interface NavLink {
  label: string;
  href: string;
  sort_order?: number;
}

const FALLBACK_LINKS: NavLink[] = [
  { label: 'Features', href: '/#features', sort_order: 1 },
  { label: 'How It Works', href: '/#how-it-works', sort_order: 2 },
  { label: 'Resources', href: '/#resources', sort_order: 3 },
];

const NavBar: React.FC = () => {
  const navigate = useNavigate();
  const [navLinks, setNavLinks] = useState<NavLink[]>(FALLBACK_LINKS);
  const [logoFailed, setLogoFailed] = useState(false);
  const { siteName, landingHeaderLogoUrl } = useSiteBranding();

  useEffect(() => {
    loadNavLinks();
  }, []);

  useEffect(() => {
    setLogoFailed(false);
  }, [landingHeaderLogoUrl]);

  const loadNavLinks = async () => {
    try {
      const config = await siteAPI.getConfig('navbar_links');
      if (config?.value && Array.isArray(config.value)) {
        const links = config.value.sort((a: NavLink, b: NavLink) =>
          (a.sort_order || 0) - (b.sort_order || 0)
        );
        setNavLinks(links);
      }
    } catch (error) {
      console.error('Failed to load navbar links:', error);
      // Use fallback links
    }
  };

  return (
    <nav
      aria-label="Main navigation"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #EEF1F6',
        height: 64,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', marginRight: 48, cursor: 'pointer' }} onClick={() => navigate('/')}>
          {!logoFailed ? (
            <img
              src={landingHeaderLogoUrl}
              alt={siteName}
              style={{ height: 48, maxWidth: 220, objectFit: 'contain', display: 'block' }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MedicineBoxOutlined style={{ fontSize: 28, color: NAVY }} />
              <span style={{ fontSize: 20, fontWeight: 700, color: NAVY, letterSpacing: -0.5 }}>
                {siteName}
              </span>
            </span>
          )}
        </div>

        {/* Nav links */}
        <div style={{ flex: 1, display: 'flex', gap: 32 }}>
          {navLinks.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              style={{ color: '#5A6577', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}
            >
              {label}
            </a>
          ))}
        </div>

        {/* Actions */}
        <Space size={12}>
          <Button
            icon={<LoginOutlined />}
            onClick={() => navigate('/login')}
            style={{ borderColor: NAVY, color: NAVY }}
          >
            登录
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => navigate('/register')}
            style={{ background: NAVY, borderColor: NAVY }}
          >
            免费注册
          </Button>
        </Space>
      </div>
    </nav>
  );
};

export default NavBar;
